import React, { useState } from 'react';
import {
  Sparkles,
  X,
  Check,
  Calendar,
  AlertCircle,
  CheckCircle,
  Clock,
  Unlock,
  ClipboardList,
  BarChart3,
  Users,
  CalendarDays,
  Package,
  Ruler,
  CheckCheck,
  Building2
} from 'lucide-react';
import { Task } from '../shared/types';
import {
  groupTasksByActivity,
  calculateCapacity,
  ActivityGroup,
  ActivitySettings
} from './planningUtils';
import { calculateTaskManpower } from '../shared/manpowerHelpers';

interface SmartPlanningAssistantProps {
  isOpen: boolean;
  onClose: () => void;
  masterPlan: Task[];
  onAddActivities: (tasks: Task[]) => void;
}

export const SmartPlanningAssistant: React.FC<SmartPlanningAssistantProps> = ({
  isOpen,
  onClose,
  masterPlan,
  onAddActivities
}) => {
  const [selectedActivities, setSelectedActivities] = useState<string[]>([]);
  const [activitySettings, setActivitySettings] = useState<Record<string, ActivitySettings>>({});
  const [selectedBuilding, setSelectedBuilding] = useState<string>('all');

  const toggleActivitySelection = (groupKey: string, group: ActivityGroup) => {
    setSelectedActivities(prev => 
      prev.includes(groupKey) ? prev.filter(k => k !== groupKey) : [...prev, groupKey]
    );
    
    // Initialize default settings for new selection
    if (!activitySettings[groupKey]) {
      setActivitySettings(prev => ({
        ...prev,
        [groupKey]: { mode: 'parallel', crews: 2, workersPerCrew: 4 }
      }));
    }
  };

  const handleClose = () => {
    onClose();
    setSelectedActivities([]);
    setActivitySettings({});
    setSelectedBuilding('all');
  };

  const handleAddActivities = () => {
    // Use filtered tasks based on selected building
    const filteredTasks = selectedBuilding === 'all' 
      ? masterPlan 
      : masterPlan.filter(task => task.building === selectedBuilding);
    
    const activityGroups = groupTasksByActivity(filteredTasks);
    const selected = activityGroups.filter(g => selectedActivities.includes(g.key));
    const tasksToAdd = selected.flatMap(g => g.tasks.map(task => ({
      ...task,
      targetQuantity: task.quantity,
      targetStartDate: task.startDate,
      targetFinishDate: task.finishDate,
      monthlyNotes: `Added via Smart Planning - ${g.name}`,
      addedToMonthly: new Date().toISOString().split('T')[0]
    })));
    
    onAddActivities(tasksToAdd);
    handleClose();
  };

  if (!isOpen) return null;

  // Extract unique buildings from master plan
  const buildings = Array.from(new Set(masterPlan.map(task => task.building).filter(Boolean))).sort();
  
  // Filter tasks by selected building
  const filteredTasks = selectedBuilding === 'all' 
    ? masterPlan 
    : masterPlan.filter(task => task.building === selectedBuilding);
  
  const activityGroups = groupTasksByActivity(filteredTasks);
  
  // Filter groups with priority (to avoid duplicates)
  const overdueGroups = activityGroups.filter(g => g.overdueCount > 0 && g.completionRate < 100);
  const overdueKeys = new Set(overdueGroups.map(g => g.key));
  
  const readyGroups = activityGroups.filter(g => 
    !overdueKeys.has(g.key) && 
    g.isReady && 
    !g.isInProgress && 
    g.completionRate < 100
  );
  const readyKeys = new Set(readyGroups.map(g => g.key));
  
  const inProgressGroups = activityGroups.filter(g => 
    !overdueKeys.has(g.key) && 
    !readyKeys.has(g.key) && 
    g.isInProgress && 
    g.completionRate > 0 && 
    g.completionRate < 100
  );
  
  // Calculate cascading work fronts based on selections
  const getOpeningActivities = () => {
    if (selectedActivities.length === 0) return [];
    
    const selected = activityGroups.filter(g => selectedActivities.includes(g.key));
    const selectedTaskIds = selected.flatMap(g => g.tasks.map(t => t.id));
    
    // Find activities that are currently blocked but would open up
    return activityGroups.filter(group => {
      // Skip if already selected
      if (selectedActivities.includes(group.key)) return false;
      // Skip if completed
      if (group.completionRate >= 100) return false;
      // Must currently be blocked
      if (!group.isBlocked) return false;
      
      // Check if ALL tasks in group would have dependencies met after selected complete
      const allTasksWouldBeReady = group.tasks.every(task => {
        if (!task.dependencies || task.dependencies.length === 0) return true;
        
        // Check if all dependencies are either:
        // 1. Already completed, OR
        // 2. In selected activities (will be completed)
        return task.dependencies.every(depId => {
          const depTask = masterPlan.find(t => t.id === depId);
          return depTask?.status === 'Completed' || selectedTaskIds.includes(depId);
        });
      });
      
      // Only include if currently blocked but would be ready
      return allTasksWouldBeReady && group.tasks.some(task => 
        task.dependencies && task.dependencies.length > 0 &&
        task.dependencies.some(depId => selectedTaskIds.includes(depId))
      );
    });
  };
  
  const openingGroups = getOpeningActivities();

  // Helper to check if a group's dependencies would be met with current selections
  const wouldDependenciesBeMet = (group: ActivityGroup) => {
    if (!group.isBlocked) return true; // Already ready
    
    const selected = activityGroups.filter(g => selectedActivities.includes(g.key));
    const selectedTaskIds = selected.flatMap(g => g.tasks.map(t => t.id));
    
    // Check if ALL tasks in group would have dependencies met
    return group.tasks.every(task => {
      if (!task.dependencies || task.dependencies.length === 0) return true;
      
      // Check if all dependencies are either:
      // 1. Already completed, OR
      // 2. In selected activities (will be completed)
      return task.dependencies.every(depId => {
        const depTask = masterPlan.find(t => t.id === depId);
        return depTask?.status === 'Completed' || selectedTaskIds.includes(depId);
      });
    });
  };

  // Calculate bottom metrics using manpower constants
  const selected = activityGroups.filter(g => selectedActivities.includes(g.key));
  let totalDays = 0;
  let totalTasks = 0;
  let totalQuantity = 0;
  let totalManDays = 0;
  let totalLaborCost = 0;
  
  // Aggregate manpower by labor type across all selected activities
  const manpowerByLabor: Record<string, { labour: string; days: number; rate: number; total: number }> = {};
  
  selected.forEach(g => {
    const settings = activitySettings[g.key] || { mode: 'parallel', crews: 2, workersPerCrew: 4 };
    const capacity = calculateCapacity(g.taskCount, g.estimatedDays, settings.crews, 30);
    const days = settings.mode === 'parallel' ? capacity.parallel.days : capacity.sequential.days;
    totalDays += days;
    totalTasks += g.taskCount;
    
    // Aggregate total quantity (cumulative)
    // For in-progress activities, use remaining quantity only
    if (g.totalQuantity > 0) {
      if (g.isInProgress && g.completionRate > 0) {
        totalQuantity += g.totalQuantity * (100 - g.completionRate) / 100;
      } else {
        totalQuantity += g.totalQuantity;
      }
    }
    
    // Calculate manpower for all tasks in this activity group
    g.tasks.forEach(task => {
      const manpower = calculateTaskManpower(task);
      if (manpower && manpower.requirements.length > 0) {
        manpower.requirements.forEach((req, idx) => {
          const cost = manpower.costs[idx];
          if (!manpowerByLabor[req.labour]) {
            manpowerByLabor[req.labour] = {
              labour: req.labour,
              days: 0,
              rate: cost.dailyRate,
              total: 0
            };
          }
          manpowerByLabor[req.labour].days += req.daysRequired;
          manpowerByLabor[req.labour].total += cost.totalCost;
          totalManDays += req.daysRequired;
          totalLaborCost += cost.totalCost;
        });
      }
    });
  });
  
  const laborCost = (totalLaborCost / 100000).toFixed(2);
  const materialCost = (totalTasks * 125000 / 100000).toFixed(2); // Keep material estimate for now
  const totalCost = ((totalLaborCost + totalTasks * 125000) / 100000).toFixed(2);
  
  // Calculate average workers per month needed
  const avgWorkersPerMonth = totalManDays > 0 ? Math.ceil(totalManDays / 26) : 0;

  const renderActivityRow = (group: ActivityGroup, colorScheme: 'red' | 'green' | 'yellow' | 'purple', statusBadge: React.ReactNode, disabled: boolean = false) => {
    const isSelected = selectedActivities.includes(group.key);
    const settings = activitySettings[group.key] || { mode: 'parallel', crews: 2, workersPerCrew: 4 };
    const capacity = calculateCapacity(group.taskCount, group.estimatedDays, settings.crews, 30);
    const days = settings.mode === 'parallel' ? capacity.parallel.days : capacity.sequential.days;
    
    const colors = {
      red: { border: 'border-red-400 bg-red-50', borderDefault: 'border-red-200', checkbox: 'text-red-600 focus:ring-red-500' },
      green: { border: 'border-green-400 bg-green-50', borderDefault: 'border-green-200', checkbox: 'text-green-600 focus:ring-green-500' },
      yellow: { border: 'border-yellow-400 bg-yellow-50', borderDefault: 'border-yellow-200', checkbox: 'text-yellow-600 focus:ring-yellow-500' },
      purple: { border: 'border-purple-400 bg-purple-50', borderDefault: 'border-purple-200', checkbox: 'text-purple-600 focus:ring-purple-500' }
    };
    
    return (
      <div
        key={group.key}
        className={`border rounded transition-colors ${
          disabled ? 'opacity-50 cursor-not-allowed' : ''
        } ${
          isSelected ? colors[colorScheme].border : `${colors[colorScheme].borderDefault} bg-white hover:${colors[colorScheme].border.split(' ')[1]}`
        }`}
      >
        <div className="px-3 py-2">
          <div className="grid items-center gap-3 text-sm" style={{ gridTemplateColumns: 'auto 1fr auto auto auto auto' }}>
            <input
              type="checkbox"
              checked={isSelected}
              onChange={() => !disabled && toggleActivitySelection(group.key, group)}
              disabled={disabled}
              className={`rounded border-gray-300 ${colors[colorScheme].checkbox} ${disabled ? 'cursor-not-allowed' : ''}`}
            />
            <div className="font-medium text-gray-900 flex items-center space-x-1.5" title={group.name}>
              <span>{group.name}</span>
              {group.isInProgress && group.completionRate > 0 && (
                <span className="text-orange-600 font-semibold text-xs">
                  ({100 - group.completionRate}%)
                </span>
              )}
            </div>
            <span className="px-1.5 py-0.5 bg-gray-100 rounded text-xs text-gray-600 text-center w-12">{group.taskCount}T</span>
            {group.totalQuantity > 0 ? (
              <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-xs text-center w-16" title={`${group.isInProgress ? 'Remaining' : 'Total'} work quantity: ${group.isInProgress ? Math.round(group.totalQuantity * (100 - group.completionRate) / 100).toLocaleString() : Math.round(group.totalQuantity).toLocaleString()} ${group.unit}`}>
                {group.isInProgress ? Math.round(group.totalQuantity * (100 - group.completionRate) / 100).toLocaleString() : Math.round(group.totalQuantity).toLocaleString()}
              </span>
            ) : (
              <span className="w-16"></span>
            )}
            {statusBadge}
            
            {isSelected && (
              <div className="flex items-center space-x-2">
                <select 
                  value={settings.mode}
                  onChange={(e) => setActivitySettings(prev => ({...prev, [group.key]: {...settings, mode: e.target.value as 'sequential' | 'parallel'}}))}
                  className="w-36 text-xs border border-gray-300 dark:border-neutral-600 rounded px-2 py-1 bg-white dark:bg-neutral-800 dark:text-neutral-100"
                >
                  <option value="sequential">Sequential ({capacity.sequential.days}d)</option>
                  <option value="parallel">Parallel ({capacity.parallel.days}d)</option>
                </select>
                <input 
                  type="number" 
                  value={settings.crews} 
                  onChange={(e) => setActivitySettings(prev => ({...prev, [group.key]: {...settings, crews: parseInt(e.target.value) || 1}}))}
                  className="w-12 text-xs border border-gray-300 rounded px-1.5 py-1 text-center"
                  min="1"
                  title="Crews"
                />
                <span className="text-xs text-gray-500">×</span>
                <input 
                  type="number" 
                  value={settings.workersPerCrew} 
                  onChange={(e) => setActivitySettings(prev => ({...prev, [group.key]: {...settings, workersPerCrew: parseInt(e.target.value) || 1}}))}
                  className="w-12 text-xs border border-gray-300 rounded px-1.5 py-1 text-center"
                  min="1"
                  title="Workers per crew"
                />
                <span className="w-14 text-xs font-medium text-blue-600 text-center">=  {settings.crews * settings.workersPerCrew}W</span>
                <span className={`w-16 text-xs font-medium px-1.5 py-0.5 rounded inline-flex items-center justify-center gap-0.5 ${days <= 30 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                  {days}d {days <= 30 ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-50"
        onClick={handleClose}
      ></div>
      
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-neutral-900 rounded-xl shadow-2xl w-[80vw] max-w-[1600px] h-[90vh] overflow-hidden flex flex-col">
          {/* Modal Header */}
          <div className="px-6 py-4 border-b border-gray-200 dark:border-neutral-700 bg-gradient-to-r from-blue-50 to-indigo-50 dark:bg-neutral-900 dark:[background-image:none] flex-shrink-0">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center space-x-3">
                  <Sparkles className="w-6 h-6 text-blue-600 dark:text-neutral-300" />
                  <h2 className="text-xl font-bold text-gray-900 dark:text-neutral-100">Smart Planning Assistant - March 2024</h2>
                </div>
                <p className="text-sm text-gray-600 dark:text-neutral-400 mt-1">Select activities and configure execution to build your monthly plan</p>
              </div>
              <button 
                onClick={handleClose}
                className="p-1 hover:bg-gray-200 dark:hover:bg-neutral-800 rounded"
              >
                <X className="w-5 h-5 dark:text-neutral-300" />
              </button>
            </div>
          </div>

          {/* Modal Body - 2 Column Layout with persistent bottom metrics */}
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Top: Two Panels */}
            <div className="flex-1 flex overflow-hidden">
              {/* LEFT PANEL - Activity Selection */}
              <div className="w-[65%] border-r border-gray-200 dark:border-neutral-800 flex flex-col">
                <div className="px-4 py-3 border-b border-gray-200 dark:border-neutral-800 bg-gray-50 dark:bg-neutral-900">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center space-x-2">
                        <ClipboardList className="w-4 h-4 text-gray-700" />
                        <h3 className="text-base font-bold text-gray-900">Select Activities</h3>
                      </div>
                      <p className="text-xs text-gray-600 ml-6">Choose and configure execution</p>
                    </div>
                    
                    {/* Building Filter */}
                    <div className="flex items-center space-x-2">
                      <Building2 className="w-4 h-4 text-gray-600" />
                      <select
                        value={selectedBuilding}
                        onChange={(e) => {
                          setSelectedBuilding(e.target.value);
                          setSelectedActivities([]); // Clear selections when changing building
                        }}
                        className="text-sm border border-gray-300 dark:border-neutral-600 rounded-lg px-3 py-1.5 bg-white dark:bg-neutral-800 dark:text-neutral-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="all">All Buildings ({buildings.length})</option>
                        {buildings.map(building => (
                          <option key={building} value={building}>
                            {building}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
                
                <div className="flex-1 overflow-y-auto p-4">
                  <div className="space-y-3">
                    {/* Overdue from Master Plan */}
                    {overdueGroups.length > 0 && (
                      <div>
                        <div className="flex items-center space-x-2 mb-2">
                          <AlertCircle className="w-4 h-4 text-red-700" />
                          <h3 className="text-sm font-bold text-red-700">OVERDUE FROM MASTER PLAN</h3>
                        </div>
                        <div className="space-y-1">
                          {overdueGroups.map(group => renderActivityRow(
                            group,
                            'red',
                            <span className="px-1.5 py-0.5 bg-red-100 text-red-700 rounded font-medium text-xs text-center w-12 flex items-center justify-center space-x-1">
                              <span>{group.overdueCount}</span>
                              <AlertCircle className="w-3 h-3" />
                            </span>,
                            !wouldDependenciesBeMet(group)
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Ready to Start */}
                    {readyGroups.length > 0 && (
                      <div>
                        <div className="flex items-center space-x-2 mb-2">
                          <CheckCircle className="w-4 h-4 text-green-700" />
                          <h3 className="text-sm font-bold text-green-700">READY TO START</h3>
                        </div>
                        <div className="space-y-1">
                          {readyGroups.map(group => renderActivityRow(
                            group,
                            'green',
                            <span className="px-1.5 py-0.5 bg-green-100 text-green-700 rounded text-xs text-center w-20 flex items-center justify-center space-x-1">
                              <CheckCircle className="w-3 h-3" />
                              <span>Ready</span>
                            </span>,
                            false
                          ))}
                        </div>
                      </div>
                    )}

                    {/* In Progress */}
                    {inProgressGroups.length > 0 && (
                      <div>
                        <div className="flex items-center space-x-2 mb-2">
                          <Clock className="w-4 h-4 text-yellow-700" />
                          <h3 className="text-sm font-bold text-yellow-700">CONTINUING</h3>
                        </div>
                        <div className="space-y-1">
                          {inProgressGroups.map(group => renderActivityRow(
                            group,
                            'yellow',
                            <span className="px-1.5 py-0.5 bg-yellow-100 text-yellow-700 rounded text-xs text-center w-20">{group.completionRate}% done</span>,
                            false
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Workfronts Opening */}
                    {openingGroups.length > 0 && (
                      <div>
                        <div className="flex items-center space-x-2 mb-2">
                          <Unlock className="w-4 h-4 text-purple-700" />
                          <h3 className="text-sm font-bold text-purple-700">WORKFRONTS OPENING (after selected complete)</h3>
                        </div>
                        <div className="space-y-1">
                          {openingGroups.map(group => renderActivityRow(
                            group,
                            'purple',
                            <span className="px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded text-xs text-center w-20 flex items-center justify-center space-x-1">
                              <Unlock className="w-3 h-3" />
                              <span>Opens</span>
                            </span>,
                            !wouldDependenciesBeMet(group)
                          ))}
                        </div>
                      </div>
                    )}

                    {activityGroups.length === 0 && (
                      <div className="text-center py-12">
                        <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                        <p className="text-gray-600">No activities available for planning</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              {/* RIGHT PANEL - Plan Summary */}
              <div className="w-[35%] flex flex-col bg-gray-50 dark:bg-neutral-950">
                <div className="px-4 py-3 border-b border-gray-200 dark:border-neutral-800 bg-white dark:bg-neutral-900">
                  <div className="flex items-center space-x-2">
                    <BarChart3 className="w-4 h-4 text-gray-700" />
                    <h3 className="text-base font-bold text-gray-900">Activity Timeline</h3>
                  </div>
                  <p className="text-xs text-gray-600 ml-6">Selected activities breakdown</p>
                </div>
                
                <div className="flex-1 overflow-y-auto p-4">
                  <div className="space-y-3">
                    {selected.length === 0 ? (
                      <div className="text-center py-16">
                        <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                        <p className="text-sm text-gray-600">Select activities to see plan</p>
                      </div>
                    ) : (
                      <>
                        {/* Manpower Breakdown by Labor Type */}
                        {Object.keys(manpowerByLabor).length > 0 && (
                          <div className="bg-white dark:bg-neutral-900 border border-gray-200 dark:border-neutral-700 rounded-lg p-3">
                            <div className="flex items-center space-x-2 mb-2">
                              <Users className="w-3.5 h-3.5 text-gray-700" />
                              <h4 className="text-xs font-bold text-gray-700">Manpower Requirements</h4>
                            </div>
                            <div className="space-y-1.5 mb-2">
                              {Object.values(manpowerByLabor).map((labor) => (
                                <div key={labor.labour} className="flex justify-between items-center text-xs">
                                  <span className="text-gray-600">{labor.labour}:</span>
                                  <div className="flex items-center space-x-2">
                                    <span className="font-medium text-blue-600">{labor.days.toFixed(1)}d</span>
                                    <span className="text-gray-400">@</span>
                                    <span className="text-gray-600">₹{labor.rate.toLocaleString()}</span>
                                    <span className="text-gray-400">=</span>
                                    <span className="font-medium text-green-600">₹{(labor.total / 1000).toFixed(1)}K</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                            <div className="border-t pt-1.5 flex justify-between items-center text-xs">
                              <span className="font-bold text-gray-700">Total:</span>
                              <div className="flex items-center space-x-2">
                                <span className="font-bold text-blue-600">{totalManDays.toFixed(1)} man-days</span>
                                <span className="text-gray-400">•</span>
                                <span className="font-bold text-green-600">₹{(totalLaborCost / 100000).toFixed(2)}L</span>
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {/* Selected Activities */}
                        <div className="bg-white dark:bg-neutral-900 border border-gray-200 dark:border-neutral-700 rounded-lg p-3">
                          <div className="flex items-center space-x-2 mb-2">
                            <ClipboardList className="w-3.5 h-3.5 text-gray-700" />
                            <h4 className="text-xs font-bold text-gray-700">Activities ({selected.length})</h4>
                          </div>
                          <div className="space-y-1">
                            {selected.map((group, idx) => {
                              const settings = activitySettings[group.key] || { mode: 'parallel', crews: 2, workersPerCrew: 4 };
                              const capacity = calculateCapacity(group.taskCount, group.estimatedDays, settings.crews, 30);
                              const days = settings.mode === 'parallel' ? capacity.parallel.days : capacity.sequential.days;
                              const workers = settings.crews * settings.workersPerCrew;
                              
                              return (
                                <div key={group.key} className="border-l-2 border-blue-400 pl-2 py-1 hover:bg-blue-50" title={group.name}>
                                  <div className="flex items-center justify-between text-xs">
                                    <span className="font-medium text-gray-900 truncate flex-1 flex items-center space-x-1">
                                      <span>{idx + 1}. {group.name}</span>
                                      {group.isInProgress && group.completionRate > 0 && (
                                        <span className="text-orange-600 font-semibold text-xs">
                                          ({100 - group.completionRate}%)
                                        </span>
                                      )}
                                    </span>
                                    <span className="text-gray-600 ml-2 whitespace-nowrap">{days}d • {workers}W</span>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Persistent Bottom Metrics Bar */}
            <div className="border-t-2 border-gray-300 dark:border-neutral-700 bg-gradient-to-r from-blue-50 to-indigo-50 dark:bg-neutral-900 dark:[background-image:none] px-6 py-3 flex items-center justify-between flex-shrink-0">
              <div className="flex items-center space-x-8">
                <div className="flex items-center space-x-2">
                  <CalendarDays className="w-4 h-4 text-gray-600" />
                  <span className="text-xs font-medium text-gray-600">Timeline:</span>
                  <span className={`text-lg font-bold ${totalDays > 30 ? 'text-red-600' : 'text-blue-600'}`}>
                    {totalDays} / 30 days
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <Users className="w-4 h-4 text-gray-600" />
                  <span className="text-xs font-medium text-gray-600">Man-days:</span>
                  <span className="text-lg font-bold text-blue-600">{totalManDays.toFixed(0)}</span>
                  {avgWorkersPerMonth > 0 && (
                    <span className="text-xs text-gray-600 ml-1">
                      (~{avgWorkersPerMonth}W/mo)
                    </span>
                  )}
                </div>
                <div className="flex items-center space-x-2">
                  <Package className="w-4 h-4 text-gray-600" />
                  <span className="text-xs font-medium text-gray-600">Tasks:</span>
                  <span className="text-lg font-bold text-blue-600">{totalTasks}</span>
                </div>
                {totalQuantity > 0 && (
                  <div className="flex items-center space-x-2">
                    <Ruler className="w-4 h-4 text-gray-600" />
                    <span className="text-xs font-medium text-gray-600">Quantity:</span>
                    <span className="text-lg font-bold text-indigo-600">
                      {Math.round(totalQuantity).toLocaleString()}
                    </span>
                  </div>
                )}
              </div>
              <div className="flex items-center space-x-6">
                <div className="text-right">
                  <div className="text-xs text-gray-600">Labor (Actual): ₹{laborCost}L • Materials (Est): ₹{materialCost}L</div>
                  <div className="text-sm font-bold text-green-600">Total Cost: ₹{totalCost}L</div>
                </div>
              </div>
            </div>
          </div>

          {/* Modal Footer */}
          <div className="px-6 py-4 border-t border-gray-200 dark:border-neutral-800 bg-gray-50 dark:bg-neutral-900 flex-shrink-0">
            <div className="flex items-center justify-between">
              <button
                onClick={handleClose}
                className="px-4 py-2 text-gray-700 dark:text-neutral-200 bg-white dark:bg-neutral-800 border border-gray-300 dark:border-neutral-600 rounded-lg hover:bg-gray-50 dark:hover:bg-neutral-700"
              >
                Cancel
              </button>

              <button
                onClick={handleAddActivities}
                disabled={selectedActivities.length === 0}
                className={`px-6 py-2 rounded-lg font-medium flex items-center space-x-2 ${
                  selectedActivities.length > 0
                    ? 'bg-green-600 text-white hover:bg-green-700'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                <CheckCheck className="w-5 h-5" />
                <span>Add {selectedActivities.length > 0 ? `${selectedActivities.length} Activities` : 'to Monthly Plan'}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

